import React, { useContext, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { ICustomItemsOfGroups, TFileExtension } from 'types';
import { getFilePathForMediaFilesWithSizeForStyleSettings } from 'backend/additionalInformation';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { FileField } from 'components/EntityForm/FileField';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';
import { FileUploadButton } from './FileUploadButton';

const savedFileName = 'menu.pdf';

interface IProps {
	item: ICustomItemsOfGroups;
	accept: TFileExtension[];
	maxFileSizeMB: number;
	onUpload: ({ fileId, sourceUrl }: { fileId: string; sourceUrl: string }) => void;
	onDelete: () => void;
	onSizeExceeded: () => void;
	onInvalidFormat: () => void;
	onUploadError: () => void;
	onViewError: () => void;
}

export const FileSectionForStyleSettingsNew: React.FC<IProps> = ({
	item,
	accept,
	maxFileSizeMB,
	onUpload,
	onDelete,
	onSizeExceeded,
	onInvalidFormat,
	onUploadError,
	onViewError,
}) => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [fileName, setFileName] = useState<string>(savedFileName);
	const hasFile = !!item.fileId || (item.fileId !== null && !!item.filePath);

	const handleUpload = ({ fileId, file }: { fileId: string; file: File }) => {
		onUpload({ fileId, sourceUrl: URL.createObjectURL(file) });
		setFileName(file.name);
	};

	const openDocument = (url: string) => {
		const link = document.createElement('a');
		link.href = url;
		link.target = '_blank';
		link.click();
	};

	const handleView = async () => {
		if (item.sourceUrl) {
			openDocument(item.sourceUrl);
			return;
		}

		try {
			showLoader();
			const { data } = await getFilePathForMediaFilesWithSizeForStyleSettings(item.translationId, 'Original');
			if (data.preSignedUrl) {
				openDocument(data.preSignedUrl);
			} else {
				onViewError();
			}
		} catch {
			onViewError();
		} finally {
			hideLoader();
		}
	};

	return (
		<div className="flex flex-col gap-2" data-test="PDFSection">
			{hasFile && <FileField fileName={fileName} testId="PDFFile" onDelete={onDelete} />}
			<div className="flex gap-4">
				<FileUploadButton
					hasFile={hasFile}
					accept={accept}
					maxFileSizeMB={maxFileSizeMB}
					testId="uploadReloadButton"
					onUpload={handleUpload}
					onSizeExceeded={onSizeExceeded}
					onInvalidFormat={onInvalidFormat}
					onUploadError={onUploadError}
				/>
				<NewButton
					size="small"
					iconLeft={<Icon name={IconName.eye} size="lg" />}
					disabled={!hasFile}
					testId="viewDocumentButton"
					onClick={handleView}>
					<Text translationNameSpace="form" translationKey="viewDocumentButton" asFragment={true} />
				</NewButton>
			</div>
		</div>
	);
};
